import React, { useEffect, useState } from "react";
import { radiusFor, cardRadiusFor } from "@/lib/designTokens";
import { resolveTokens } from "@/lib/designTokensV2";

/* Template Live Preview — CERMIN storefront renderer.

   Sprint: Template Design System v2 (Phase 3).
   Preview MUST show a visible representation of every one of the 10 canonical
   tokens. Reads via `resolveTokens(design)` (legacy fold + defaults). */
export const TemplateLivePreview = ({ design = {}, dna = {}, demoContent = {} }) => {
  const d = design || {};
  const T = resolveTokens(d);
  const font = d.typography_body || "Inter";
  const headingFont = d.typography_heading || font;
  const brand = demoContent?.brand_name || d.seo_title || "Nama Brand";
  const sections = (dna?.homepage_sections || []).slice(0, 5);
  const products = demoContent?.products || [];
  const categories = demoContent?.categories || [];
  const banners = (demoContent?.banners || []).slice(0, 5);
  const isSlider = banners.length > 1 && dna?.banner_style !== "static";
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    if (!isSlider) return undefined;
    const t = setInterval(() => setSlide((s) => (s + 1) % banners.length), 3000);
    return () => clearInterval(t);
  }, [isSlider, banners.length]);
  const banner = banners[isSlider ? slide % banners.length : 0];
  const btnRadius = radiusFor(d.button_style);
  const cardRadius = cardRadiusFor(d.product_card_style);
  const gridCols = (dna?.product_layout === "grid-2-col") ? "grid-cols-2"
    : (dna?.product_layout === "grid-3-col") ? "grid-cols-3" : "grid-cols-4";
  const premium = d.homepage_layout === "premium-commerce";
  const heroFull = d.homepage_layout === "hero-full" || premium;
  const headerStyle = d.header_style || "sticky-with-search";
  const showSearch = headerStyle !== "simple" && headerStyle !== "centered-logo";
  const footerStyle = d.footer_style || "columns-4";
  const footerCompact = footerStyle === "minimal" || footerStyle === "centered" || footerStyle === "premium-compact";
  const perRow = dna?.product_layout === "grid-2-col" ? 2 : dna?.product_layout === "grid-3-col" ? 3 : 4;

  const productsFor = (idx) => {
    if (products.length === 0) return Array.from({ length: perRow }, () => null);
    const start = (idx * perRow) % products.length;
    return Array.from({ length: perRow }, (_, i) => products[(start + i) % products.length]);
  };

  const catLabel = (c) => (typeof c === "string" ? c : (c?.name || c?.slug || "Kategori"));
  const hideOnError = (e) => { e.currentTarget.style.display = "none"; };

  const renderHeroContent = (b) => (
    <div className={`absolute inset-0 flex flex-col justify-center px-4 ${b?.text_alignment === "center" ? "items-center text-center" : b?.text_alignment === "right" ? "items-end text-right" : ""}`}>
      {b?.badge && <span className="text-[7px] uppercase font-bold text-white px-1.5 py-0.5 rounded-full mb-0.5 w-fit" style={{ backgroundColor: T.primary }}>{b.badge}</span>}
      <div className="text-white font-black text-sm" style={{ fontFamily: headingFont }}>{b?.title || "Judul Banner"}</div>
      <div className="text-white/85 text-[10px]">{b?.subtitle || "Subjudul promo Anda di sini"}</div>
      {b?.button_text && (
        <span className="mt-1 w-fit text-[8px] font-bold bg-white px-2 py-0.5" style={{ color: "#1A1A1A", borderRadius: btnRadius }}>{b.button_text}</span>
      )}
    </div>
  );

  // Sample sale product — first product with sale_price, or fabricated.
  const saleSample = products.find((p) => p && (p.sale_price || p.discount_pct)) || null;
  const salePctFromSample = saleSample && saleSample.price && saleSample.sale_price
    ? Math.round((1 - Number(saleSample.sale_price) / Number(saleSample.price)) * 100)
    : 20;

  return (
    <div
      className="rounded-2xl overflow-hidden border border-white/15 shadow-2xl"
      data-testid="tpl-live-preview"
      style={{ backgroundColor: T.background, color: T.text_primary, fontFamily: font }}
    >
      {/* -------- Header — uses surface + border + text_primary + primary CTA -------- */}
      <div
        className="flex items-center gap-2 px-3 py-2.5 border-b"
        data-testid="tpl-preview-header"
        data-header-style={headerStyle}
        style={{
          backgroundColor: T.surface,
          borderColor: T.border,
          justifyContent: headerStyle === "centered-logo" ? "space-between" : "flex-start",
        }}
      >
        {demoContent?.logo_url || d.logo_url ? (
          <img src={demoContent?.logo_url || d.logo_url} alt="logo" onError={hideOnError} className="w-6 h-6 rounded object-cover" />
        ) : (
          <div className="w-6 h-6 rounded" style={{ backgroundColor: T.primary }} />
        )}
        <span className="text-sm font-black truncate" style={{ fontFamily: headingFont, color: T.text_primary }} data-testid="tpl-preview-brand">{brand}</span>
        {showSearch && (
          /* Uses surface_secondary + border + text_secondary — proves those 3 tokens */
          <div
            className="flex-1 max-w-[55%] ml-1 rounded-lg px-2.5 py-1 text-[9px] truncate border"
            data-testid="tpl-preview-search"
            style={{ backgroundColor: T.surface_secondary, borderColor: T.border, color: T.text_secondary }}
          >
            Cari produk…
          </div>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: T.border }} />
          <span
            className="text-[8px] font-bold text-white px-2 py-1"
            data-testid="tpl-preview-cta-primary"
            style={{ backgroundColor: T.primary, borderRadius: btnRadius }}
          >
            Masuk
          </span>
        </div>
      </div>

      {/* -------- Hero — background gradient primary→accent (accent visible) -------- */}
      {banners.length === 0 ? (
        <div
          className={`relative overflow-hidden grid place-items-center h-24 ${heroFull ? "" : "mx-3 mt-3 rounded-xl"}`}
          style={{ background: `linear-gradient(135deg, color-mix(in srgb, ${T.primary} 18%, ${T.background}), color-mix(in srgb, ${T.accent} 20%, ${T.background}))` }}
          data-testid="tpl-preview-banner-fallback"
        >
          <div className="text-center px-4">
            <div className="font-black text-sm" style={{ fontFamily: headingFont, color: T.text_primary }}>{brand}</div>
            <div className="text-[9px]" style={{ color: T.text_secondary }}>{d.business_description || "Belanja mudah, langsung dari toko kami"}</div>
            <span className="mt-1 inline-block text-[8px] font-bold text-white px-2 py-0.5" style={{ backgroundColor: T.primary, borderRadius: btnRadius }}>Belanja Sekarang</span>
          </div>
        </div>
      ) : (
        <div
          className={`relative overflow-hidden ${heroFull ? "" : "mx-3 mt-3 rounded-xl"}`}
          style={{ background: `linear-gradient(120deg, ${T.primary}, ${T.accent})` }}
          data-testid="tpl-preview-banner"
          data-slide-count={banners.length}
          data-homepage-layout={d.homepage_layout || "banner-sections-grid"}
        >
          {banner?.image_url ? (
            <img src={banner.image_url} alt="" onError={hideOnError} className={`w-full object-cover transition-opacity duration-500 ${heroFull ? "h-28" : "h-24"}`} />
          ) : (
            <div className={heroFull ? "h-28" : "h-24"} />
          )}
          {premium ? null : <div className="absolute inset-0 bg-black/30" />}
          {premium ? null : renderHeroContent(banner)}
          {isSlider && (
            <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1" data-testid="tpl-preview-banner-dots">
              {banners.map((_, i) => (
                <span key={i} className="h-1.5 rounded-full transition-all duration-300" style={{ width: i === slide % banners.length ? 10 : 6, backgroundColor: i === slide % banners.length ? "#fff" : "rgba(255,255,255,0.4)" }} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* -------- Design System v2 — Explicit token showcase strip (all 10 tokens visible) -------- */}
      <div
        className="mx-3 mt-3 rounded-xl p-2.5 border"
        data-testid="tpl-preview-token-showcase"
        style={{ backgroundColor: T.surface, borderColor: T.border }}
      >
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: T.text_secondary }}>
            Token Preview
          </span>
          <span className="text-[8px] font-black px-1.5 py-0.5 rounded" style={{ backgroundColor: T.sale, color: "#fff" }} data-testid="tpl-preview-sale-badge">
            -{salePctFromSample}%
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {/* Primary + Primary-Dark swatch pair (hover state visible without hovering) */}
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] font-bold text-white px-2 py-1 rounded" data-testid="tpl-preview-primary-btn" style={{ backgroundColor: T.primary }}>CTA</span>
            <span className="text-[9px] font-bold text-white px-2 py-1 rounded" data-testid="tpl-preview-primary-dark-btn" title="Primary hover / active" style={{ backgroundColor: T.primary_dark }}>Hover</span>
          </div>
          {/* Accent — non-primary emphasis chip */}
          <div className="flex items-center gap-1.5 justify-end">
            <span
              className="text-[9px] font-bold px-2 py-1 rounded border"
              data-testid="tpl-preview-accent-chip"
              style={{ color: T.accent, borderColor: T.accent, backgroundColor: `color-mix(in srgb, ${T.accent} 12%, transparent)` }}
            >
              Terlaris
            </span>
          </div>
          {/* Secondary surface (input example) */}
          <div
            className="col-span-2 rounded-lg px-2.5 py-1.5 text-[10px] border"
            data-testid="tpl-preview-secondary-surface"
            style={{ backgroundColor: T.surface_secondary, borderColor: T.border, color: T.text_secondary }}
          >
            Contoh input / panel sekunder
          </div>
          {/* Sale + discount price example */}
          {saleSample ? (
            <div className="col-span-2 text-[10px] flex items-center gap-2" data-testid="tpl-preview-sale-price">
              <span className="font-black" style={{ color: T.sale }}>
                Rp {Number(saleSample.sale_price || 0).toLocaleString("id-ID")}
              </span>
              <span className="line-through" style={{ color: T.text_secondary }}>
                Rp {Number(saleSample.price || 0).toLocaleString("id-ID")}
              </span>
            </div>
          ) : (
            <div className="col-span-2 text-[10px] flex items-center gap-2" data-testid="tpl-preview-sale-price">
              <span className="font-black" style={{ color: T.sale }}>Rp 80.000</span>
              <span className="line-through" style={{ color: T.text_secondary }}>Rp 100.000</span>
            </div>
          )}
        </div>
      </div>

      {/* -------- Trust badges mini — surface + border -------- */}
      <div className="grid grid-cols-3 gap-1.5 px-3 mt-2.5">
        {["Pengiriman Cepat", "Kualitas Terjamin", "Harga Terbaik"].map((tt) => (
          <div key={tt} className="rounded-lg border px-1.5 py-1.5 flex items-center gap-1" style={{ backgroundColor: T.surface, borderColor: T.border }}>
            <div className="w-3.5 h-3.5 rounded shrink-0" style={{ backgroundColor: `color-mix(in srgb, ${T.primary} 15%, ${T.surface})` }} />
            <span className="text-[7px] font-bold" style={{ color: T.text_primary }}>{tt}</span>
          </div>
        ))}
      </div>

      {/* -------- Kategori circles -------- */}
      {categories.length > 0 && (
        <div className="flex gap-2 px-3 mt-2.5 overflow-hidden" data-testid="tpl-preview-categories">
          {categories.slice(0, 5).map((c, i) => (
            <div key={i} className="flex flex-col items-center gap-0.5 min-w-[38px]">
              <div className="w-8 h-8 rounded-xl grid place-items-center text-[11px]" style={{ backgroundColor: `color-mix(in srgb, ${T.primary} 12%, ${T.surface})` }}>🛍️</div>
              <span className="text-[7px] font-semibold truncate max-w-[40px]" style={{ color: T.text_primary }}>{catLabel(c)}</span>
            </div>
          ))}
        </div>
      )}

      {/* -------- Inventory units chips — primary outline -------- */}
      {(dna?.inventory_units || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-3 mt-2.5" data-testid="tpl-preview-units">
          {(dna.inventory_units || []).map((u) => (
            <span key={u} className="text-[9px] font-bold px-2 py-0.5 rounded-full border" style={{ borderColor: T.primary, color: T.primary }}>{u}</span>
          ))}
        </div>
      )}

      {/* -------- Homepage Sections -------- */}
      <div className="px-3 py-3 space-y-4" data-testid="tpl-preview-sections" data-product-layout={dna?.product_layout || "grid-4-col"}>
        {(sections.length ? sections : ["Produk Unggulan"]).map((sec, idx) => (
          <div key={`${sec}-${idx}`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-black" style={{ fontFamily: headingFont, color: T.text_primary }} data-testid={`tpl-preview-section-${idx}-title`}>{sec}</span>
              <span className="text-[9px] font-bold" style={{ color: T.primary }}>Lihat semua →</span>
            </div>
            <div className={`grid ${gridCols} gap-2`}>
              {productsFor(idx).map((p, i) => (
                <div
                  key={i}
                  className="border overflow-hidden relative"
                  data-card-style={d.product_card_style || "rounded"}
                  style={{ borderColor: T.border, borderRadius: cardRadius, backgroundColor: T.surface }}
                >
                  {p?.image_url ? (
                    <img src={p.image_url} alt="" onError={hideOnError} className="w-full h-12 object-cover" />
                  ) : (
                    <div className="h-12" style={{ background: `linear-gradient(135deg, ${T.primary}33, ${T.accent}33)` }} />
                  )}
                  {/* One product per section gets a sale badge to prove --sale */}
                  {i === 0 && idx === 0 && (
                    <span
                      className="absolute top-1 left-1 text-[7px] font-black text-white px-1 py-0.5 rounded"
                      style={{ backgroundColor: T.sale }}
                    >
                      -{salePctFromSample}%
                    </span>
                  )}
                  <div className="p-1.5">
                    <div className="text-[9px] font-bold truncate" style={{ color: T.text_primary }}>{p?.name || "Nama Produk"}</div>
                    <div className="text-[9px] font-black" style={{ color: T.primary }}>
                      {p ? `Rp ${Number(p.price || 0).toLocaleString("id-ID")}` : "Rp 0"}
                      <span className="font-normal" style={{ color: T.text_secondary }}> /{p?.unit || (dna?.inventory_units || ["pcs"])[0]}</span>
                    </div>
                    <div className="mt-1 text-center text-[7px] font-bold py-0.5" style={{ backgroundColor: `color-mix(in srgb, ${T.primary} 12%, ${T.surface})`, color: T.primary, borderRadius: btnRadius }}>+ Tambah</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {sections.length === 0 && (
          <div className="text-[10px] text-center py-1" style={{ color: T.text_secondary }}>{d.empty_state_text || "homepage_sections kosong — storefront memakai section default."}</div>
        )}
        <button className="w-full py-1.5 text-[10px] font-black text-white" style={{ backgroundColor: T.primary, borderRadius: btnRadius }}>
          Belanja Sekarang
        </button>
      </div>

      {/* -------- Footer — surface + border + text_secondary + primary accents -------- */}
      <div
        className={`px-4 py-3 text-[9px] border-t ${footerCompact ? "text-center" : ""}`}
        data-testid="tpl-preview-footer"
        data-footer-style={footerStyle}
        style={{ backgroundColor: T.surface, borderColor: T.border, color: T.text_secondary }}
      >
        {footerCompact ? (
          <>
            <div className="font-black mb-0.5" style={{ color: T.text_primary }}>{brand}</div>
            <div className="line-clamp-1">{d.business_description || "Deskripsi bisnis Anda tampil di sini."}</div>
          </>
        ) : (
          <div className={`grid gap-2 ${footerStyle === "columns-2" ? "grid-cols-2" : "grid-cols-3"}`}>
            <div className={footerStyle === "columns-2" ? "" : "col-span-1"}>
              <div className="font-black mb-0.5" style={{ color: T.text_primary }}>{brand}</div>
              <div className="line-clamp-2">{d.business_description || "Deskripsi bisnis Anda tampil di sini."}</div>
            </div>
            <div>
              <div className="font-bold uppercase text-[7px] mb-0.5" style={{ color: T.primary }}>Belanja</div>
              <div>Semua Produk</div>
            </div>
            {footerStyle !== "columns-2" && (
              <div>
                <div className="font-bold uppercase text-[7px] mb-0.5" style={{ color: T.primary }}>Bantuan</div>
                <div>WhatsApp · Email</div>
              </div>
            )}
          </div>
        )}
        <div className="mt-1.5 pt-1.5 border-t text-center opacity-70" style={{ borderColor: T.border }}>© {new Date().getFullYear()} {brand}</div>
      </div>
    </div>
  );
};

export default TemplateLivePreview;
